"use client";

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

/**
 * Shopping cart context — LocalStorage-baseret klient-state.
 *
 * Cart persisteres i localStorage så brugeren ikke mister varer mellem sider
 * eller browser-restart. Sync til Supabase/customer-account sker først i
 * Step 17 (checkout) når vi har en kunde-record.
 */

export type CartItem = {
  product_id: string;
  slug: string;
  name: string;
  price_dkk: number; // effective_price (allerede kr-integer)
  quantity: number;
  image_url: string | null;
  image_alt: string | null;
};

type CartState = {
  items: CartItem[];
  couponCode: string | null;
};

type CartContextValue = {
  items: CartItem[];
  couponCode: string | null;
  itemCount: number;
  subtotal: number;
  isHydrated: boolean;
  /** UI: er drawer-popup åben? */
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  setCouponCode: (code: string | null) => void;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "broendby_cart_v1";
const EMPTY_STATE: CartState = { items: [], couponCode: null };

function loadCart(): CartState {
  if (typeof window === "undefined") return EMPTY_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY_STATE;
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      couponCode: typeof parsed.couponCode === "string" ? parsed.couponCode : null,
    };
  } catch {
    return EMPTY_STATE;
  }
}

function saveCart(state: CartState): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Fuld localStorage eller privacy-mode — ignorér
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  // Start med tom state for at undgå hydration-mismatch
  const [state, setState] = useState<CartState>(EMPTY_STATE);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Hydrér fra localStorage efter mount
  useEffect(() => {
    setState(loadCart());
    setIsHydrated(true);
  }, []);

  // Persist ved enhver ændring (efter hydration)
  useEffect(() => {
    if (isHydrated) saveCart(state);
  }, [state, isHydrated]);

  // Listen for andre tabs der ændrer cart
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setState(loadCart());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem: CartContextValue["addItem"] = useCallback((item, quantity = 1) => {
    setState((prev) => {
      const existing = prev.items.find((i) => i.product_id === item.product_id);
      if (existing) {
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.product_id === item.product_id ? { ...i, quantity: i.quantity + quantity } : i
          ),
        };
      }
      return {
        ...prev,
        items: [...prev.items, { ...item, quantity }],
      };
    });
    // Auto-åbn drawer når der lægges noget i — giver visuel feedback
    setIsOpen(true);
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((p) => !p), []);

  const removeItem: CartContextValue["removeItem"] = useCallback((productId) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.product_id !== productId),
    }));
  }, []);

  const updateQuantity: CartContextValue["updateQuantity"] = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setState((prev) => ({ ...prev, items: prev.items.filter((i) => i.product_id !== productId) }));
      return;
    }
    setState((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.product_id === productId ? { ...i, quantity } : i)),
    }));
  }, []);

  const setCouponCode: CartContextValue["setCouponCode"] = useCallback((code) => {
    setState((prev) => ({ ...prev, couponCode: code }));
  }, []);

  const clear = useCallback(() => setState(EMPTY_STATE), []);

  const itemCount = useMemo(() => state.items.reduce((sum, i) => sum + i.quantity, 0), [state.items]);
  const subtotal = useMemo(
    () => state.items.reduce((sum, i) => sum + i.price_dkk * i.quantity, 0),
    [state.items]
  );

  const value: CartContextValue = {
    items: state.items,
    couponCode: state.couponCode,
    itemCount,
    subtotal,
    isHydrated,
    isOpen,
    openCart,
    closeCart,
    toggleCart,
    addItem,
    removeItem,
    updateQuantity,
    setCouponCode,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

/**
 * Format DKK-pris (kroner-integer) til dansk visning.
 */
export function formatDkk(dkk: number, locale: "da" | "en" = "da"): string {
  return new Intl.NumberFormat(locale === "en" ? "en-GB" : "da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(dkk);
}

/**
 * Format DKK med 2 decimaler — bruges til moms-linjer og fakturaer hvor
 * præcision er vigtig (særligt for små beløb hvor heltal-afrunding ville
 * vise 0 kr).
 */
export function formatDkkPrecise(dkk: number, locale: "da" | "en" = "da"): string {
  return new Intl.NumberFormat(locale === "en" ? "en-GB" : "da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dkk);
}
